import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from './Toast';
import { api } from '../lib/api';
import { tr } from '../lib/i18nHelpers';
import TxPostingPanel from './TxPostingPanel';
import { buildCoaTree } from '../lib/coa-hierarchy';

interface PostingProp {
  entry_id: string;
  entry_number: string;
  entry_source: string;
  lines: { account_code: string; account_name: string; debit: number; credit: number }[];
}

/**
 * Editable GL posting for one invoice — the wiring around TxPostingPanel.
 * Used both in the AR/AP View-detail modal (via InvoiceDetailPanel) and as the
 * inline editor that opens when a Dr/Cr chip is clicked in the AR/AP lists.
 *
 * AR (outgoing): Dr holding = asset receivable (editable), Cr label = revenue
 * lines. AP (incoming): Dr label = expense/cost lines, Cr holding = liability
 * payable (editable). Label lines must sum to the invoice total; the holding
 * line mirrors it, so the entry always balances (enforced server-side too).
 */
export default function InvoicePostingEditor({
  invoiceId,
  direction,
  total,
  posting,
}: {
  invoiceId: string;
  direction?: string | null;
  total: number;
  posting: PostingProp | null;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: accountsData } = useQuery({
    queryKey: ['invoice-posting-accounts'],
    queryFn: () => api('/bookkeeping/accounts?include_inactive=false&limit=1000'),
  });
  const accounts: any[] = accountsData?.data || [];

  const isIncoming = direction === 'incoming';
  const labelTypes = isIncoming
    ? ['expense', 'other_expense', 'cost']
    : ['revenue', 'other_income'];
  const labelTree = buildCoaTree(accounts.filter((a: any) => labelTypes.includes(a.account_type)));
  const holdingTree = buildCoaTree(accounts.filter((a: any) => a.account_type === (isIncoming ? 'liability' : 'asset')));
  const fixedSideLine = posting
    ? (posting.lines.find(l => (isIncoming ? l.credit > 0 : l.debit > 0)) || null)
    : null;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
    queryClient.invalidateQueries({ queryKey: ['entries'] });
    queryClient.invalidateQueries({ queryKey: ['bank-statements'] });
    queryClient.invalidateQueries({ queryKey: ['invoices-ar'] });
    queryClient.invalidateQueries({ queryKey: ['invoices-ap'] });
    queryClient.invalidateQueries({ queryKey: ['invoices'] });
  };

  const savePosting = async (lines: { account_code: string; amount: number }[], holdingCode?: string) => {
    await api(`/invoices/${invoiceId}/posting`, {
      method: 'PUT',
      body: { lines, ...(holdingCode ? { holding_account_code: holdingCode } : {}) },
    });
    refresh();
    toast.success(tr('Posting updated', '分錄已更新', '分录已更新'));
  };

  const resetPosting = async () => {
    await api(`/invoices/${invoiceId}/posting`, { method: 'PUT', body: { reset_to_auto: true } });
    refresh();
    toast.info(tr('Reset to automatic categorization', '已重設為自動分類', '已重设为自动分类'));
  };

  return (
    <TxPostingPanel
      kind="invoice"
      movementAmount={total}
      contraSide={isIncoming ? 'Dr' : 'Cr'}
      // Placeholder policy 2026-11-04: invoices hold on the AR/AP control
      // accounts (was temp suspense 21901/11901).
      fixedCode={fixedSideLine?.account_code || (isIncoming ? '21101' : '11201')}
      fixedName={fixedSideLine?.account_name || (isIncoming ? 'Accounts Payable 應付賬款' : 'Accounts Receivable 應收賬款')}
      posting={posting}
      accounts={accounts}
      tree={labelTree}
      holdingEditable
      holdingTree={holdingTree}
      onSave={savePosting}
      onResetAuto={resetPosting}
    />
  );
}
